import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import TransMexLogo from "../../../assets/TransMexLogo-Final_CMYK.png";
import AuthService from "../../../Services/AuthService";
import EncryptionUtils from "../../../Utils/EncryptionUtils";
import { decodeMessage } from "../../../Utils/HeadersMethods";
import UserData from "../../../Components/Account/Login/UserData";
import i18n from "../../../i18n";

const REMEMBER_EMAIL_KEY = "transmexRememberedEmail";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "#f8fafc",
    transition: "box-shadow 0.2s ease, background-color 0.2s ease",
    "&.Mui-focused": {
      bgcolor: "#fff",
      boxShadow: "0 0 0 4px rgba(7, 70, 142, 0.12)",
    },
  },
};

export default function Login({ onLogin, config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isRemembered, setIsRemembered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const authService = useMemo(() => new AuthService(), []);

  const currentLanguage = (i18n.language || "es").split("-")[0];
  const primary = theme.palette.primary.main;
  const nav = theme.palette.nav?.main ?? "#000922";

  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    if (savedEmail) {
      setEmail(savedEmail);
      setIsRemembered(true);
    }
  }, []);

  const handleLanguageChange = (_, lang) => {
    if (!lang) return;
    localStorage.setItem("language", lang);
    i18n.changeLanguage(lang);
  };

  const clearFieldErrors = () => {
    setEmailError("");
    setPasswordError("");
    setLoginError("");
  };

  const handleLogin = async () => {
    setLoading(true);
    setLoginError("");

    try {
      const result = await authService.login(
        `${config.url}/api/User/login`,
        email.trim(),
        password,
        isRemembered
      );

      if (result?.data !== undefined) {
        if (isRemembered) {
          localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim());
        } else {
          localStorage.removeItem(REMEMBER_EMAIL_KEY);
        }

        const userDataUser = new UserData(result.data);
        localStorage.setItem(
          "permissions",
          JSON.stringify(userDataUser.user.permisosWebUsuarios)
        );

        if (result.data.User.IdEmpresa) {
          localStorage.setItem("empresa", result.data.User.IdEmpresa);
        }

        if (result.data.Language) {
          const idiomaBase = result.data.Language.split("-")[0];
          localStorage.setItem("language", idiomaBase);
          await i18n.changeLanguage(idiomaBase);
        } else {
          const idiomaBase = userDataUser.idLanguage === 1 ? "es" : "en";
          localStorage.setItem("language", idiomaBase);
          await i18n.changeLanguage(idiomaBase);
        }

        EncryptionUtils.storeEncryptedData("user", JSON.stringify(userDataUser));
        onLogin(userDataUser);
      } else {
        setLoginError(t("account.login.loginError"));
      }
    } catch (err) {
      const headerMessage = err?.response?.headers?.message;
      if (headerMessage !== undefined) {
        setLoginError(decodeMessage(headerMessage));
      } else {
        setLoginError(t("account.login.loginError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    clearFieldErrors();

    let isValid = true;

    if (!email.trim()) {
      setEmailError(t("account.login.emailRequired"));
      isValid = false;
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      setEmailError(t("validation.emailFormat"));
      isValid = false;
    }

    if (!password) {
      setPasswordError(t("account.login.passwordRequired"));
      isValid = false;
    }

    if (isValid) {
      handleLogin();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(145deg, ${nav} 0%, ${primary} 45%, #1a8fd4 100%)`,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 2,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={currentLanguage}
          onChange={handleLanguageChange}
          sx={{
            bgcolor: "rgba(255,255,255,0.14)",
            backdropFilter: "blur(12px)",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.22)",
            "& .MuiToggleButton-root": {
              color: "rgba(255,255,255,0.9)",
              border: 0,
              px: 2,
              py: 0.6,
              fontWeight: 600,
              fontSize: "0.8rem",
              textTransform: "none",
              "&.Mui-selected": {
                bgcolor: "#fff",
                color: primary,
                "&:hover": { bgcolor: "#fff" },
              },
            },
          }}
        >
          <ToggleButton value="es">ES</ToggleButton>
          <ToggleButton value="en">EN</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: { xs: 3, sm: 4.5 },
          py: { xs: 4, sm: 5 },
          borderRadius: { xs: 3.5, sm: 4 },
          bgcolor: "#fff",
          boxShadow: "0 32px 64px rgba(0, 9, 34, 0.28)",
          border: "1px solid rgba(255,255,255,0.5)",
        }}
      >
          <Box
            component="img"
            src={TransMexLogo}
            alt={t("navigation.logoAlt")}
            sx={{
              display: "block",
              mx: "auto",
              maxWidth: "100%",
              width: "auto",
              height: "auto",
              maxHeight: 80,
              objectFit: "contain",
              mb: 3,
            }}
          />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
              {t("account.login.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
              {t("account.login.subtitle")}
            </Typography>
          </Box>

          {loginError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              {loginError}
            </Alert>
          )}

          <Stack spacing={2.25}>
            <TextField
              variant="outlined"
              fullWidth
              id="email"
              label={t("account.login.email")}
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError("");
                if (loginError) setLoginError("");
              }}
              error={!!emailError}
              helperText={emailError}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon color="primary" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              variant="outlined"
              fullWidth
              name="password"
              label={t("account.login.password")}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError("");
                if (loginError) setLoginError("");
              }}
              error={!!passwordError}
              helperText={passwordError}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon color="primary" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword
                          ? t("account.login.hidePassword")
                          : t("account.login.showPassword")
                      }
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            sx={{ mt: 1.5 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="isRemembered"
                  checked={isRemembered}
                  onChange={(e) => setIsRemembered(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2">{t("account.login.rememberMe")}</Typography>
              }
              sx={{ userSelect: "none", m: 0 }}
            />
            <Link
              component={RouterLink}
              to="/Account/ForgotPassword"
              variant="body2"
              underline="hover"
              fontWeight={600}
              sx={{ color: primary }}
            >
              {t("account.login.forgotPassword")}
            </Link>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.55,
              borderRadius: 2.5,
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
              boxShadow: `0 8px 24px ${alpha(primary, 0.35)}`,
            }}
          >
            {loading ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} color="inherit" />
                <span>{t("account.login.signingIn")}</span>
              </Stack>
            ) : (
              t("account.login.enter")
            )}
          </Button>

          <Divider sx={{ my: 2.5 }} />

          <Typography align="center" variant="body2" color="text.secondary">
            <Link
              component={RouterLink}
              to="/Account/AvisoPrivacidad"
              underline="hover"
              fontWeight={600}
              sx={{ color: primary }}
            >
              {t("account.login.privacyNotice")}
            </Link>
          </Typography>
      </Box>
    </Box>
  );
}
